/**
 * Teacher: problem CRUD page (simple version).
 * GET /api/problems[?tag=], POST /api/problems, DELETE /api/problems/:id
 */
import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';

interface ProblemRow {
  id: string;
  title: string;
  body: string;
  answer: string;
  z3_formula: string | null;
  tags: string[];
}

export function ProblemList() {
  const [problems, setProblems] = useState<ProblemRow[]>([]);
  const [tagFilter, setTagFilter] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [answer, setAnswer] = useState('');
  const [formula, setFormula] = useState('');
  const [tags, setTags] = useState('');

  async function load() {
    try {
      const path = tagFilter ? `/api/problems?tag=${encodeURIComponent(tagFilter)}` : '/api/problems';
      const data = await api.get<{ problems: ProblemRow[] }>(path);
      setProblems(data.problems);
    } catch (e) {
      setError((e as Error).message);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tagFilter]);

  async function handleCreate(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/problems', {
        title: title.trim(),
        body: body.trim(),
        answer: answer.trim(),
        z3_formula: formula.trim() || null,
        tags: tags
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean),
      });
      setTitle('');
      setBody('');
      setAnswer('');
      setFormula('');
      setTags('');
      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete(id: string) {
    if (!confirm('정말 삭제할까요?')) return;
    try {
      await api.delete(`/api/problems/${id}`);
      await load();
    } catch (e) {
      setError((e as Error).message);
    }
  }

  return (
    <main className="page">
      <Link to="/teacher" className="back-link">대시보드</Link>
      <h1 className="mb-6">Problem Bank</h1>

      <div className="form-section">
        <form onSubmit={handleCreate} className="form-stack" data-testid="create-problem-form" aria-label="문제 추가">
          <div>
            <label htmlFor="problem-title" className="sr-only">제목</label>
            <input
              id="problem-title"
              data-testid="problem-title"
              className="input"
              placeholder="제목"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />
          </div>
          <div>
            <label htmlFor="problem-body" className="sr-only">문제 본문</label>
            <textarea
              id="problem-body"
              data-testid="problem-body"
              className="input"
              placeholder="문제 본문"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              required
              rows={3}
            />
          </div>
          <div className="form-row">
            <div className="flex-1">
              <label htmlFor="problem-answer" className="sr-only">정답</label>
              <input
                id="problem-answer"
                data-testid="problem-answer"
                className="input"
                placeholder="정답 (예: 2, 3)"
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                required
              />
            </div>
            <div className="flex-1">
              <label htmlFor="problem-formula" className="sr-only">z3 수식</label>
              <input
                id="problem-formula"
                data-testid="problem-formula"
                className="input"
                placeholder="z3 수식 (선택)"
                value={formula}
                onChange={(e) => setFormula(e.target.value)}
              />
            </div>
          </div>
          <div>
            <label htmlFor="problem-tags" className="sr-only">태그</label>
            <input
              id="problem-tags"
              data-testid="problem-tags"
              className="input"
              placeholder="태그 (쉼표 구분)"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
            />
          </div>
          <button
            data-testid="create-problem-submit"
            type="submit"
            disabled={busy}
            className="btn btn-gold self-start"
            aria-busy={busy}
          >
            {busy ? '저장 중…' : '문제 추가'}
          </button>
        </form>
      </div>

      <div className="mb-6">
        <label htmlFor="tag-filter" className="sr-only">태그 필터</label>
        <input
          id="tag-filter"
          data-testid="tag-filter"
          className="input max-w-filter"
          placeholder="태그로 필터링…"
          value={tagFilter}
          onChange={(e) => setTagFilter(e.target.value)}
        />
      </div>

      {error && (
        <p data-testid="problem-error" className="error-text mb-4" role="alert">
          {error}
        </p>
      )}

      {problems.length === 0 && !error && (
        <div className="empty-state">
          <p>등록된 문제가 없습니다</p>
          <p className="list-item-meta mt-2">위 양식으로 첫 문제를 만들어 보세요</p>
        </div>
      )}

      <ul className="list-stack" data-testid="problem-list" aria-label="문제 목록">
        {problems.map((p) => (
          <li key={p.id} className="list-item" data-testid={`problem-${p.id}`}>
            <div className="list-item-header">
              <strong>{p.title}</strong>
              <button
                data-testid={`delete-${p.id}`}
                onClick={() => handleDelete(p.id)}
                className="btn btn-danger"
                aria-label={`${p.title} 삭제`}
              >
                삭제
              </button>
            </div>
            <div className="list-item-body">{p.body}</div>
            <div className="list-item-meta mt-2">
              정답: <code>{p.answer}</code>
            </div>
            {p.z3_formula && (
              <div className="list-item-meta">
                수식: <code>{p.z3_formula}</code>
              </div>
            )}
            {p.tags?.length > 0 && (
              <div className="tag-row">
                {p.tags.map((t) => (
                  <span key={t} className="tag">{t}</span>
                ))}
              </div>
            )}
          </li>
        ))}
      </ul>
    </main>
  );
}
